"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useRef, startTransition, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useRouter } from "next/navigation";
import ConfirmLeaveDialog from "@/components/confirm-leave-dialog";
import { markSoftNavStart } from "@/lib/telemetry";
type SaveOnLeaveFn = (() => Promise<boolean | void> | boolean | void) | undefined;
type BlockerEntry = {
  id: number;
  onSave?: SaveOnLeaveFn;
};
const NavigationBlockerContext = createContext<{
  blockedCount: number;
  canSaveOnLeave: boolean;
  registerBlocker: (onSave?: SaveOnLeaveFn) => () => void;
  requestNavigationConfirmation: () => Promise<boolean>;
  runSaveAndLeave: () => Promise<boolean>;
} | null>(null);
function useNavigationBlockerContext() {
  const context = useContext(NavigationBlockerContext);
  if (!context) {
    throw new Error("Navigation blocker context is missing.");
  }
  return context;
}
export function NavigationBlockerProvider({
  children
}: {
  children: ReactNode;
}) {
  const idRef = useRef(0);
  const [entries, setEntries] = useState<BlockerEntry[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const pendingResolversRef = useRef<Array<(result: boolean) => void>>([]);
  const blockedCount = entries.length;
  const activeEntry = entries[entries.length - 1];
  const canSaveOnLeave = Boolean(activeEntry?.onSave);
  const resolvePending = useCallback((result: boolean) => {
    const resolvers = pendingResolversRef.current;
    pendingResolversRef.current = [];
    resolvers.forEach(resolve => resolve(result));
    setIsDialogOpen(false);
  }, []);
  const registerBlocker = useCallback((onSave?: SaveOnLeaveFn) => {
    const id = ++idRef.current;
    setEntries(prev => [...prev, {
      id,
      onSave
    }]);
    return () => {
      setEntries(prev_0 => prev_0.filter(entry => entry.id !== id));
    };
  }, []);
  const requestNavigationConfirmation = useCallback(() => {
    if (blockedCount === 0) {
      return Promise.resolve(true);
    }
    return new Promise<boolean>(resolve_0 => {
      pendingResolversRef.current.push(resolve_0);
      setIsDialogOpen(true);
    });
  }, [blockedCount]);
  const runSaveAndLeave = useCallback(async () => {
    if (!activeEntry?.onSave) {
      resolvePending(true);
      return true;
    }
    setIsSaving(true);
    try {
      const result_0 = await activeEntry.onSave();
      if (result_0 === false) {
        return false;
      }
      resolvePending(true);
      return true;
    } catch (error) {
      // The failing save is a mutation — its translated error toast is shown by
      // the global handler (see providers.tsx); don't surface the raw message.
      console.error(error);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [activeEntry, resolvePending]);
  const contextValue = useMemo(() => ({
    blockedCount,
    canSaveOnLeave,
    registerBlocker,
    requestNavigationConfirmation,
    runSaveAndLeave
  }), [blockedCount, canSaveOnLeave, registerBlocker, requestNavigationConfirmation, runSaveAndLeave]);
  return <NavigationBlockerContext.Provider value={contextValue}>
      {children}
      <ConfirmLeaveDialog open={isDialogOpen} canSave={canSaveOnLeave} isSaving={isSaving} onCancel={() => resolvePending(false)} onLeave={() => resolvePending(true)} onSaveAndLeave={() => {
      void runSaveAndLeave();
    }} />
    </NavigationBlockerContext.Provider>;
}
export function useIsBlocked() {
  const {
    blockedCount
  } = useNavigationBlockerContext();
  return blockedCount > 0;
}
export function useConfirmNavigation() {
  const $ = _c(2);
  const {
    requestNavigationConfirmation
  } = useNavigationBlockerContext();
  let t0;
  if ($[0] !== requestNavigationConfirmation) {
    t0 = async () => requestNavigationConfirmation();
    $[0] = requestNavigationConfirmation;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function Blocker({
  onSave
}: {
  onSave?: SaveOnLeaveFn;
}) {
  const {
    registerBlocker
  } = useNavigationBlockerContext();
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  useEffect(() => {
    // Register exactly once per mount. Reading `onSave` from a ref keeps the
    // latest callback available without making an unstable (inline) `onSave`
    // identity re-run this effect — that churn would unregister/re-register on
    // every render and, because consumers of this context re-render on the
    // resulting count change, spin into "Maximum update depth exceeded".
    const hasSave = Boolean(onSaveRef.current);
    return registerBlocker(hasSave ? () => onSaveRef.current?.() : undefined);
  }, [registerBlocker]);
  return null;
}
export function BlockBrowserNavigation() {
  const $ = _c(7);
  const isBlocked = useIsBlocked();
  const confirmNavigation = useConfirmNavigation();
  let t0;
  let t1;
  if ($[0] !== confirmNavigation || $[1] !== isBlocked) {
    t0 = () => {
      if (!isBlocked) {
        return;
      }
      let ignoreNextPopState = false;
      let isHandling = false;
      const handlePopState = () => {
        if (ignoreNextPopState) {
          ignoreNextPopState = false;
          return;
        }
        ignoreNextPopState = true;
        window.history.go(1);
        if (isHandling) {
          return;
        }
        isHandling = true;
        (async () => {
          const shouldLeave = await confirmNavigation();
          if (shouldLeave) {
            ignoreNextPopState = true;
            window.history.back();
          }
          isHandling = false;
        })();
      };
      window.addEventListener("popstate", handlePopState);
      return () => {
        window.removeEventListener("popstate", handlePopState);
      };
    };
    t1 = [confirmNavigation, isBlocked];
    $[0] = confirmNavigation;
    $[1] = isBlocked;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] !== isBlocked) {
    t2 = () => {
      if (!isBlocked) {
        return;
      }
      const showModal = _temp;
      window.addEventListener("beforeunload", showModal);
      return () => {
        window.removeEventListener("beforeunload", showModal);
      };
    };
    t3 = [isBlocked];
    $[4] = isBlocked;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  return null;
}
function _temp(event) {
  event.preventDefault();
  event.returnValue = "";
}
export function useBlockableRouter() {
  const $ = _c(10);
  const router = useRouter();
  const confirmNavigation = useConfirmNavigation();
  let t0;
  if ($[0] !== confirmNavigation || $[1] !== router) {
    t0 = (...t1) => {
      const args = t1;
      const [, options] = args;
      (async () => {
        if (!options?.ignoreBlocker && !(await confirmNavigation())) {
          return;
        }
        markSoftNavStart();
        startTransition(() => {
          router.push(...args);
        });
      })();
    };
    $[0] = confirmNavigation;
    $[1] = router;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const push = t0;
  let t1;
  if ($[3] !== confirmNavigation || $[4] !== router) {
    t1 = (...t2) => {
      const args_0 = t2;
      const [, options_0] = args_0;
      (async () => {
        if (!options_0?.ignoreBlocker && !(await confirmNavigation())) {
          return;
        }
        markSoftNavStart();
        startTransition(() => {
          router.replace(...args_0);
        });
      })();
    };
    $[3] = confirmNavigation;
    $[4] = router;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const replace = t1;
  let t2;
  if ($[6] !== push || $[7] !== replace || $[8] !== router) {
    t2 = {
      ...router,
      push,
      replace
    };
    $[6] = push;
    $[7] = replace;
    $[8] = router;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}